import { useState, useEffect } from "react"
import { onCardCollapse } from "../../events/collapseCard"
import { Separator } from "../generic/Core"

import styled from "styled-components"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Icon_Drag } from "../../styles/icons"
import { processLineDefaults, convertObjectToMessage } from "../../utils/defaults/processDefaults"

import CoreForm from "./CoreForm"
import LineDocumentsButton from "../documents/LineDocumentsButton"
import { showLineDocuments } from "../documents/documentLineState"
import Collapse from "../generic/Collapse"
import Display from '../display/Display'

//Get current Country from Profile and use Valtio to read a snapshot
import useProfile from "../../hooks/useProfile";
import useTranslation from "../../hooks/useTranslation";
import { snapshot } from "valtio";

//Run Business Logic
import runLogic from '../../../../common/logic/runLogic'
import { getNestedValue, setNestedValue } from "../../utils/objects/nestedValue"

const ItemWrapper = styled.div`
    margin-inline: -1rem;
    margin-top: -1rem;

    border-top-right-radius: ${(props) => props.$idx === 0 ? '0.90rem' : ''};
    border-top-left-radius: ${(props) => props.$idx === 0 ? '0.90rem' : ''};

  &:hover{
    background-color: ${({ theme }) => theme.table.hover};
  }  
`

const DisplayWrapper = styled.div`
    margin-left: 1.5rem;
    margin-right: 1rem;
    padding-top: 1rem;
`

const LineWrapper = styled.div`
    position: relative;
`

// A line with documents (`meta.lineDocuments`) keeps its button beside it.
const LineRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.25rem;
`

const LineMain = styled.div`
    flex: 1 1 auto;
    min-width: 0;
`

const DragHandle = styled.div`
    position: absolute;
    left: -1.25rem;
    top: 50%;
    transform: translateY(-50%);
    cursor: grab;
    color: ${({ theme }) => theme.text?.secondary || '#aaa'};
    display: flex;
    align-items: center;
    padding: 0.25rem;
    touch-action: none;
    opacity: 0.4;

    &:hover {
        opacity: 1;
    }

    &:active {
        cursor: grabbing;
    }
`

const LineElement = ({ idx, sortableId, section, dispatch, validated, entity, useStore, linesArray = 'lines', path = [], options }) => {
    const [active, setActive] = useState(false)
    const { t } = useTranslation()

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId || `line-${idx}` })
    
    // Get state based on path used for context state in the modal
    const [state, setState] = useStore(s => getNestedValue(s, path))
    // Get full state used for the business logic
    const [fullState, setFullState, getFullState] = useStore(s => s)
    const prevData = { ...fullState }

    const lineId = idx

    //Getting the State
    const { pState } = useProfile()
    const profile = snapshot(pState)

    /*
     * A line closes onto its display and the document's totals, so both must
     * be current — however it closes. A field can write without the chain:
     * the VAT picker fills its default as the line opens, and closing the line
     * with its toggle then left the line at 25 % and the totals at 0 % VAT
     * (seen 2026-10-09). Read through the getter: that write is newer than
     * this render.
     */
    const closeLine = () => {
        setActive(false)
        const current = getFullState()
        setFullState({ ...runLogic(current, current.entity, current), savedOn: new Date() })
    }

    const lineDocuments = section.meta.lineDocuments

    // An opened line is the one the documents panel shows.
    const openLine = () => {
        setActive(true)
        if (lineDocuments) showLineDocuments(idx)
    }

    const toggleActive = () => {
        if (active) closeLine()
        else openLine()
    }

    // An open line closes with the rest of the card (collapseCard).
    useEffect(() => onCardCollapse(entity, () => setActive(false)), [entity])

    // if it is empty is opened
    useEffect(
        () => {
            //setActive(true)
            if (state[linesArray] && Object.keys(state[linesArray][idx]).length === 0) {
                setActive(true)
                //This code adds the defaults to a line object
                const defaults = processLineDefaults(section, profile)
                let line = { ...state[linesArray][idx], ...defaults }
                let lines = [...state[linesArray]]
                lines.splice(idx, 1, line)
                //It then updates all lines
                setState({ [linesArray]: lines })
            }
        }, []

    )

    const toggleSection = () => { }

    /*
     * Writing through the full state and re-running the chain, the way Save
     * does: a discount changes the line total, the VAT groups and the document
     * totals, so a patch that only touched the line would leave the rest stale.
     */
    const applyToLine = async (changes) => {
        const lines = [...(getNestedValue(fullState, [...path, linesArray]) || [])]
        lines.splice(idx, 1, { ...lines[idx], ...changes })

        const next = setNestedValue(fullState, [...path, linesArray], lines)
        const processedData = await runLogic(next, next.entity, prevData)
        setFullState({ ...processedData, savedOn: new Date() })
    }

    const localDispatch = async (action, localFormData) => {
        if (action === 'close') {
            closeLine()
        } else if (action === 'save') {           
            const processedData = await runLogic(fullState, fullState.entity, prevData)
            setFullState({ ...processedData, savedOn: new Date() })
            setActive(false)
        } else if (action === 'delete') {
            setActive(false)
            dispatch(action, localFormData, idx)
        } else if (action === 'addLineDiscount') {
            // A type and a zero, which is what makes the fields appear — the menu
            // item sets state rather than toggling a view flag.
            await applyToLine({ discountType: 'percentage', discountValue: 0 })
        } else if (action === 'removeLineDiscount') {
            // null, never '': the schema's enum admits null and rejects the empty
            // string, and null is what "there is no discount here" means.
            await applyToLine({ discountType: null, discountValue: null })
        } else {
            dispatch(action, localFormData, idx)
        }
    }

    // Build the full path for this line
    const fullPath = [...path, linesArray, idx]

    const sortableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    const displayOnly = !!section.meta.displayOnly

    const lineDisplay = (
        <Display
            section={section}
            currency={state?.currency || ''}
            clickAction={displayOnly ? undefined : toggleActive}
            type='lines'
            idx={idx}
            path={fullPath}
            divided={false}
        >
        </Display>
    )

    return (
        <LineWrapper ref={setNodeRef} style={sortableStyle}>
            {!active && !displayOnly && (
                <DragHandle {...attributes} {...listeners}>
                    {Icon_Drag}
                </DragHandle>
            )}
            {!displayOnly && <Collapse name="Collapse-Button" expanded={active} toggleExpanded={toggleActive}
                top='0.275rem' right='-0.575rem' testId={`${section.id}.section.line.${idx+1}.toggle`}
            ></Collapse>}
            <ItemWrapper name='LineFormDisplay' $idx={idx}>
                <DisplayWrapper>
                    {
                        !active &&
                        <>
                            {lineDocuments
                                ? <LineRow>
                                    <LineMain>{lineDisplay}</LineMain>
                                    <LineDocumentsButton lineDocuments={lineDocuments} index={idx} />
                                </LineRow>
                                : lineDisplay}

                            <Separator></Separator>
                        </>
                    }
                </DisplayWrapper>
            </ItemWrapper>
            <>
                {
                    active && !displayOnly && <>
                        <CoreForm
                            section={section}
                            lineId={lineId}
                            dispatch={localDispatch}
                            validated={validated}
                            toggleSection={toggleSection}
                            expanded={true}
                            entity={entity}
                            useStore={useStore}
                            path={fullPath}
                            options = {options}
                        />
                        <Separator></Separator>
                    </>
                }
            </>
        </LineWrapper>
    )
}

export default LineElement
